/**
 * The parse boundary.
 *
 * A response body is `unknown` the instant it leaves `JSON.parse` — the network
 * does not enforce the shape a schema promises. The estimate response in
 * particular (§6.6) is a deeply nested numeric tree: without a boundary that
 * rejects a mismatch outright, a field the server renamed would come through
 * as `undefined`, render as a blank cell, and nobody would notice for a week
 * (S12). `parseOrThrow` is that boundary — every parsed response goes through
 * it rather than being cast.
 */

import type { ZodType } from 'zod';

/**
 * Thrown by `parseOrThrow` when `schema` rejects `value`. Carries the
 * `context` the caller passed in and one human-readable line per Zod issue,
 * each prefixed with its dotted path — `stats.pages: Expected array, received
 * string` — so the offending field is named, not just "the response".
 */
export class ParseError extends Error {
  constructor(
    readonly context: string,
    readonly issues: string[],
  ) {
    super(`${context}: response did not match the expected shape — ${issues.join('; ')}`);
    this.name = 'ParseError';
  }
}

/** Parses at the boundary. Throws `ParseError` naming both `context` and the
 * dotted path of every offending field, rather than returning `undefined` for
 * a field a schema no longer recognises. */
export function parseOrThrow<T>(schema: ZodType<T>, value: unknown, context: string): T {
  const result = schema.safeParse(value);
  if (result.success) {
    return result.data;
  }

  const issues = result.error.issues.map((issue) => {
    const path = issue.path.length > 0 ? issue.path.join('.') : '(root)';
    return `${path}: ${issue.message}`;
  });
  throw new ParseError(context, issues);
}
